import { useCallback, useEffect, useState } from "react";
import { api, API, fmtInt } from "@/lib/api";
import { Plus, Minus, Stack, FunnelSimple, Printer, FileXls, FilePdf } from "@phosphor-icons/react";
import MovementModal from "@/components/inventory/MovementModal";

export default function Inventory() {
  const [types, setTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [items, setItems] = useState([]);
  const [log, setLog] = useState([]);
  const [modal, setModal] = useState(null); // 'in' | 'out' | null

  const load = useCallback(async () => {
    const params = selectedTypes.length > 0 ? { types: selectedTypes.join(",") } : {};
    const [a, b] = await Promise.all([
      api.get("/inventory", { params }),
      api.get("/inventory/log", { params: { days: 30 } }),
    ]);
    setItems(a.data);
    setLog(b.data);
  }, [selectedTypes]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/inventory/types").then(({ data }) => setTypes(data)).catch(() => setTypes([]));
  }, []);

  const toggleType = (code) => {
    setSelectedTypes((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);
  };

  const total = items.reduce((s, i) => s + (i.stock || 0), 0);
  const logFiltered = selectedTypes.length > 0 ? log.filter((l) => !l.type || selectedTypes.includes(l.type)) : log;

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="overline text-zinc-500 mb-2">CONTROLE DE ESTOQUE</div>
          <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Inventário de Copos</h1>
        </div>
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="overline text-zinc-500">TOTAL EM ESTOQUE{selectedTypes.length > 0 ? " · FILTRADO" : ""}</div>
            <div className="font-mono text-4xl font-semibold mt-1" style={{ color: total < 0 ? "#E60000" : "#00A300" }} data-testid="inventory-total">{fmtInt(total)}</div>
          </div>
          <div className="flex gap-2">
            <button className="btn py-4 px-6" style={{ background: "#00A300", borderColor: "#00A300" }} onClick={() => setModal("in")} data-testid="inv-entry-btn">
              <Plus size={18} weight="bold" /> ENTRADA
            </button>
            <button className="btn btn-danger py-4 px-6" onClick={() => setModal("out")} data-testid="inv-exit-btn">
              <Minus size={18} weight="bold" /> SAÍDA
            </button>
          </div>
        </div>
      </div>

      {/* Botões de relatório */}
      <div className="border border-black bg-white p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="inventory-report-bar">
        <div className="flex items-center gap-2 text-zinc-500">
          <Printer size={16} weight="bold" />
          <span className="overline">RELATÓRIO DE ESTOQUE</span>
        </div>
        <button className="btn btn-ghost text-sm" onClick={() => window.open("/inventory/print", "_blank")} data-testid="report-print-btn">
          <Printer size={16} weight="bold" /> Impressão
        </button>
        <a
          className="btn btn-ghost text-sm no-underline"
          href={`${API}/reports/inventory/export?format=xlsx&_t=${Date.now()}`}
          onClick={(e) => {
            e.preventDefault();
            const url = `${API}/reports/inventory/export?format=xlsx`;
            fetch(url, { credentials: "include" })
              .then((r) => r.blob())
              .then((blob) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `estoque_${new Date().toISOString().slice(0,10)}.xlsx`;
                link.click();
              });
          }}
          data-testid="report-xlsx-btn"
        >
          <FileXls size={16} weight="bold" /> Excel
        </a>
        <a
          className="btn btn-ghost text-sm no-underline"
          href={`${API}/reports/inventory/export?format=pdf`}
          onClick={(e) => {
            e.preventDefault();
            const url = `${API}/reports/inventory/export?format=pdf`;
            fetch(url, { credentials: "include" })
              .then((r) => r.blob())
              .then((blob) => {
                const link = document.createElement("a");
                link.href = URL.createObjectURL(blob);
                link.download = `estoque_${new Date().toISOString().slice(0,10)}.pdf`;
                link.click();
              });
          }}
          data-testid="report-pdf-btn"
        >
          <FilePdf size={16} weight="bold" /> PDF
        </a>
      </div>

      {/* Type filter — multi select */}
      <div className="border border-black bg-white p-4 mb-6" data-testid="inventory-type-filter">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-zinc-500">
            <FunnelSimple size={16} weight="bold" />
            <span className="overline">FILTRAR POR TIPO</span>
          </div>
          <button
            className={`px-3 py-1.5 border text-xs font-bold uppercase tracking-widest transition-colors ${selectedTypes.length === 0 ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}
            onClick={() => setSelectedTypes([])}
            data-testid="filter-type-all"
          >
            Todos
          </button>
          {types.map((t) => (
            <button
              key={t.code}
              className={`px-3 py-1.5 border text-xs font-bold uppercase tracking-widest transition-colors ${selectedTypes.includes(t.code) ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}
              onClick={() => toggleType(t.code)}
              data-testid={`filter-type-${t.code}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-black bg-white mb-8" data-testid="inventory-list">
        <div className="border-b border-black p-4 flex justify-between">
          <div className="overline text-zinc-500">ESTOQUE POR TIPO E COR</div>
          <Stack size={20} weight="bold" />
        </div>
        {items.length === 0 ? (
          <div className="p-10 text-center text-zinc-400">Sem itens no estoque. Clique em ENTRADA para começar.</div>
        ) : (
          <table className="table">
            <thead><tr><th>Tipo</th><th>Cor</th><th className="text-right">Estoque</th><th className="text-right">Atualizado</th></tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={`${it.type}-${it.color}`} data-testid={`inv-row-${it.type}-${it.color}`}>
                  <td><span className="text-xs px-2 py-0.5 bg-zinc-100 border border-zinc-300 uppercase tracking-widest">{it.type_label || it.type}</span></td>
                  <td className="font-semibold">{it.color}</td>
                  <td className={`text-right font-mono text-xl ${(it.stock || 0) < 0 ? "text-[#E60000] font-bold" : ""}`}>{fmtInt(it.stock)}</td>
                  <td className="text-right text-xs text-zinc-500">{it.updated_at ? new Date(it.updated_at).toLocaleString("pt-BR") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="border border-black bg-white" data-testid="inventory-log">
        <div className="border-b border-black p-4 overline text-zinc-500">HISTÓRICO DE MOVIMENTAÇÕES · ÚLTIMOS 30 DIAS</div>
        {logFiltered.length === 0 ? (
          <div className="p-10 text-center text-zinc-400">Nenhuma movimentação recente.</div>
        ) : (
          <table className="table">
            <thead><tr><th>Data</th><th>Tipo</th><th>Cor</th><th>Delta</th><th>Estoque após</th><th>Origem</th><th>Colaborador</th><th>Obs</th></tr></thead>
            <tbody>
              {logFiltered.map((l) => (
                <tr key={`${l.at}-${l.type}-${l.color}-${l.delta}`}>
                  <td className="font-mono text-xs">{new Date(l.at).toLocaleString("pt-BR")}</td>
                  <td className="text-xs">{l.type_label || l.type || "—"}</td>
                  <td>{l.color}</td>
                  <td className={`font-mono font-bold ${l.delta > 0 ? "text-[#00A300]" : "text-[#E60000]"}`}>
                    {l.delta > 0 ? "+" : ""}{l.delta}
                  </td>
                  <td className={`font-mono ${(l.stock_after || 0) < 0 ? "text-[#E60000] font-bold" : ""}`}>{l.stock_after}</td>
                  <td>
                    {l.source === "order" ? (
                      <span className="text-[10px] px-2 py-0.5 bg-black text-white uppercase tracking-widest">Pedido</span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 bg-zinc-100 border border-zinc-300 uppercase tracking-widest">Manual</span>
                    )}
                  </td>
                  <td className="font-semibold">{l.actor}</td>
                  <td className="text-zinc-500 text-xs">{l.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <MovementModal
          mode={modal}
          types={types}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}
